import { Button } from '@/app/common/components/button/Button'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { Input } from '@/app/common/components/input/Input'
import FormErrorMessage from '@/common/components/FormErrorMessage'
import SimpleSelector from '@/common/components/SimpleSelector'
import { ChevronDownIcon } from '@/app/common/icons/arrows'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/shad/ui/dialog'
import { RadioGroup, RadioGroupItem } from '@/shad/ui/radio-group'
import { useToast } from '@/shad/ui/toast/use-toast'
import useCreateBankAccount from '@/queries/bank_accounts/hooks/useCreateBankAccount'
import { Processor } from '@/queries/bank_accounts/schema'

const CreateBankAccountFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  processor_id: z
    .number({ required_error: 'Processor is required' })
    .min(1, 'Processor is required'),
  status: z.union([z.literal(0), z.literal(1)]),
})
type CreateBankAccountFormValues = z.infer<typeof CreateBankAccountFormSchema>

interface CreateBankAccountDialogProps {
  programId: number
  programName: string
  availableProcessors: Processor[]
  onSuccess: () => void
}

function CreateBankAccountDialog({
  programId,
  programName,
  availableProcessors,
  onSuccess,
}: CreateBankAccountDialogProps) {
  const [isOpen, setIsOpen] = useState(false)
  const { toast } = useToast()

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<CreateBankAccountFormValues>({
    resolver: zodResolver(CreateBankAccountFormSchema),
    defaultValues: { status: 1 },
  })

  const createMutation = useCreateBankAccount({
    onSuccess: () => {
      toast({ title: 'Bank account created successfully' })
      onSuccess()
      setIsOpen(false)
      reset()
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Error creating bank account',
        description: error.message,
      })
    },
  })

  const onSubmit = handleSubmit((data) => {
    createMutation.mutate({
      program_id: programId,
      name: data.name,
      v2_locations_processors_id: data.processor_id,
      status: data.status,
    })
  })

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
        if (!open) reset()
      }}
    >
      <DialogTrigger asChild>
        <Button size="lg" className="font-bold">
          Create Bank Account
        </Button>
      </DialogTrigger>
      <DialogContent className="space-y-4">
        <DialogTitle>Create Bank Account</DialogTitle>
        <form onSubmit={onSubmit} noValidate className="space-y-3">
          <div>
            <label className="block text-sm font-bold leading-6 text-gray-900">
              Program
            </label>
            <div className="mt-2">
              <Input value={programName} disabled />
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold leading-6 text-gray-900 mb-2">
              Name<span className="text-danger ml-1">*</span>
            </label>
            <Input
              {...register('name')}
              type="text"
              placeholder="Enter bank account name"
            />
            <FormErrorMessage message={errors.name?.message} />
          </div>

          <div>
            <label className="block text-sm font-bold leading-6 text-gray-900 mb-2">
              Processor<span className="text-danger ml-1">*</span>
            </label>
            <Controller
              control={control}
              name="processor_id"
              render={({ field }) => {
                const processorOptions = availableProcessors.map((p) => ({
                  id: p.id,
                  name: `${p.name} - MID ${p.merchantId}`,
                }))
                return (
                  <SimpleSelector
                    list={processorOptions}
                    selected={
                      processorOptions.find((p) => p.id === field.value) ?? null
                    }
                    setSelected={(p) => field.onChange(p.id)}
                    icon={<ChevronDownIcon className="text-lg" />}
                    placeholder={
                      processorOptions.length === 0
                        ? 'No available processors'
                        : 'Select a processor'
                    }
                  />
                )
              }}
            />
            <FormErrorMessage message={errors.processor_id?.message} />
          </div>

          <div>
            <label className="block text-sm font-bold leading-6 text-gray-900 mb-2">
              Status<span className="text-danger ml-1">*</span>
            </label>
            <Controller
              control={control}
              name="status"
              render={({ field }) => (
                <RadioGroup
                  value={String(field.value)}
                  onValueChange={(v) => field.onChange(Number(v))}
                  className="flex flex-col space-y-1 mt-1"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="1" id="create-status-active" />
                    <label htmlFor="create-status-active" className="text-sm">
                      Active
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="0" id="create-status-inactive" />
                    <label htmlFor="create-status-inactive" className="text-sm">
                      Inactive
                    </label>
                  </div>
                </RadioGroup>
              )}
            />
            {errors.status && (
              <p className="text-sm text-red-500 mt-1">
                {errors.status.message}
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsOpen(false)
                reset()
              }}
            >
              Cancel
            </Button>
            <SubmitButton status={createMutation.status} buttonTxt="Create" />
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default CreateBankAccountDialog
